import { ReactNode, useEffect, useRef, useState } from "react";
import styled, { createGlobalStyle, keyframes, ThemeProvider } from "styled-components";

import { FutureMoneyDarkLogo } from "@components/Header/FutureMoneyLogo/FutureMoneyDarkLogo";
import { AnalyticsEvent } from "@ctypes";
import { Analytics } from "@services";
import { useThemeStore } from "@stores";
import { getRouteForPath } from "@utils";
import { useLocation } from "./navigation";
import { EntryShellContext } from "./EntryShellContext";
import { entryTheme } from "./entryTheme";
import { ENTRY_VIDEO_BACKGROUND, entryColors, prefersReducedMotion } from "./tokens";
import { BackArrowIcon } from "./ui";

const EntryGlobalStyle = createGlobalStyle`
  body {
    background: ${entryColors.navy};
  }

  ::selection {
    background: rgba(247, 182, 150, 0.45);
  }
`;

const Wrapper = styled.div`
  position: fixed;
  inset: 0;
  overflow-y: auto;
  overflow-x: hidden;
  background: ${entryColors.navy};
  color: ${entryColors.textPrimary};
  -webkit-font-smoothing: antialiased;
`;

const drift = keyframes`
  0% { transform: translate3d(-3%, -2%, 0) scale(1.06); }
  50% { transform: translate3d(3%, 2%, 0) scale(1.12); }
  100% { transform: translate3d(-3%, -2%, 0) scale(1.06); }
`;

/**
 * Always-painted fallback recreating the mood of onboarding-background.mp4
 * (textured wall with a warm moving light). Guarantees an on-brand first
 * paint and a graceful experience if the video can't load or play.
 */
const GradientFallback = styled.div`
  position: fixed;
  inset: -6%;
  background:
    radial-gradient(58% 44% at 38% 38%, rgba(143, 82, 53, 0.4) 0%, rgba(143, 82, 53, 0) 100%),
    radial-gradient(70% 55% at 72% 72%, rgba(103, 64, 42, 0.32) 0%, rgba(103, 64, 42, 0) 100%),
    linear-gradient(160deg, #0b0d22 0%, #201720 45%, #2a1d1a 70%, #150f1c 100%);
  animation: ${drift} 36s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const BackgroundVideo = styled.video<{ $visible: boolean }>`
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 1200ms ease;
  pointer-events: none;
`;

const OverlaySvg = styled.svg`
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

const Content = styled.div`
  position: relative;
  z-index: 1;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  padding: 0 24px;
`;

const LogoPill = styled.div`
  margin-top: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 13px 30px;
  border-radius: 50px;
  background: rgba(20, 20, 34, 0.55);
  border: 1px solid rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
`;

/* Sized rather than transform: scale()d — under the transform the layout box
   stayed at the unscaled 21px and the logo bled into the content below it.
   The clamps are shares of the landing canvas, matching WelcomeScreen. */
const BareLogo = styled.div`
  margin-top: clamp(18px, 3.91vh, 46px);

  svg {
    display: block;
    height: clamp(18px, 2.80vh, 33px);
    width: auto;
    max-width: 60vw;
  }
`;

const BackButton = styled.button`
  position: fixed;
  top: 30px;
  left: 26px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(20, 20, 34, 0.45);
  border: 1px solid rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  cursor: pointer;
  transition: background 160ms ease;

  &:hover {
    background: rgba(255, 255, 255, 0.14);
  }

  &:focus-visible {
    outline: 2px solid ${entryColors.highlight};
    outline-offset: 2px;
  }
`;

/**
 * Port of mobile DarkOverlay (src/gradients/DarkOverlay.tsx): a 75% navy
 * scrim with a warm radial glow rising from below the fold.
 */
const DarkOverlay = () => (
  <OverlaySvg preserveAspectRatio="none" aria-hidden="true">
    <defs>
      <radialGradient id="entry-warm-glow" cx="50%" cy="108.24%" r="95%">
        <stop offset="0" stopColor="rgb(143, 82, 53)" stopOpacity="0.4" />
        <stop offset="1" stopColor="rgb(34, 21, 5)" stopOpacity="0" />
      </radialGradient>
    </defs>
    <rect width="100%" height="100%" fill={entryColors.scrim} />
    <rect width="100%" height="100%" fill="url(#entry-warm-glow)" />
  </OverlaySvg>
);

export type EntryShellProps = {
  children: ReactNode;
  header?: "pill" | "bare" | "none";
  onBack?: () => void;
};

export const EntryShell = ({ children, header = "pill", onBack }: EntryShellProps) => {
  const [videoReady, setVideoReady] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const showVideo = !videoFailed && !prefersReducedMotion();
  const location = useLocation();

  // Every entry/onboarding screen backs out through this button rather than
  // the shared Header one, so without this the flow's back presses go
  // untracked. location.pathname is the current step inside the flow.
  const handleBack = () => {
    Analytics.track(AnalyticsEvent.BackButtonPressed, {
      screen: getRouteForPath(location.pathname),
    });
    onBack?.();
  };

  // Components read the theme two ways: styled-components' ThemeProvider
  // (nested below) and the useTheme() hook backed by the zustand store.
  // Keep both on the entry theme while the shell is mounted; restore the
  // user's theme when the flow hands off to the main app.
  useEffect(() => {
    const previous = useThemeStore.getState().theme;
    useThemeStore.getState().setTheme(entryTheme);

    return () => {
      useThemeStore.getState().setTheme(previous);
    };
  }, []);

  useEffect(() => {
    if (!showVideo) return;
    // Some browsers block autoplay despite `muted`; treat a rejected play()
    // as a signal to stay on the gradient rather than showing a frozen frame.
    const video = videoRef.current;
    const playPromise = video?.play();
    playPromise?.catch(() => setVideoFailed(true));
  }, [showVideo]);

  return (
    <EntryShellContext.Provider value={true}>
      <ThemeProvider theme={entryTheme}>
        <Wrapper>
          <EntryGlobalStyle />
          <GradientFallback aria-hidden="true" />
          {showVideo && (
            <BackgroundVideo
              ref={videoRef}
              $visible={videoReady}
              src={ENTRY_VIDEO_BACKGROUND}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              aria-hidden="true"
              onCanPlay={() => setVideoReady(true)}
              onError={() => setVideoFailed(true)}
            />
          )}
          <DarkOverlay />

          {onBack && (
            <BackButton type="button" aria-label="Go back" onClick={handleBack}>
              <BackArrowIcon />
            </BackButton>
          )}

          <Content>
            {header === "pill" && (
              <LogoPill>
                <FutureMoneyDarkLogo />
              </LogoPill>
            )}
            {header === "bare" && (
              <BareLogo>
                <FutureMoneyDarkLogo />
              </BareLogo>
            )}
            {children}
          </Content>
        </Wrapper>
      </ThemeProvider>
    </EntryShellContext.Provider>
  );
};
